import config from "../../../../config";
import { otherDefaultFeesPrettyNames } from "../../../../components/modals/add-new-application-modal/pricing-validation";
import {
  getAllDefaultACHValues,
  getAllDefaultPricingModelValues,
  pricingModelRequiresDailyBilling,
} from "./pricing-defaults";

// Optional mid/non-qual + AMEX tiers 2–4 — blanked for Kurv CC apps.
export const KURV_OPTIONAL_TIERED_FIELDS = [
  "tiered_mid_qual_rate",
  "tiered_mid_qual_transaction",
  "tiered_mid_qual_debit_rate",
  "tiered_mid_qual_debit_per_transaction",
  "tiered_non_qual_rate",
  "tiered_non_qual_transaction",
  "tiered_non_qual_debit_rate",
  "tiered_non_qual_debit_per_transaction",
  "amex_tier_two_rate",
  "amex_tier_two_fee",
  "amex_tier_three_rate",
  "amex_tier_three_fee",
  "amex_tier_four_rate",
  "amex_tier_four_fee",
];

export const isKurvProcessorId = (id) =>
  Number(id) === Number(config.paymentcloud.KURV_PROCESSOR_ID);

export const hasKurvProcessor = (processors) =>
  (processors ?? []).some(isKurvProcessorId);

export const isAchProcessorId = (id) =>
  (config.paymentcloud.ACH_PROCESSOR_IDS ?? []).some(
    (achId) => Number(achId) === Number(id),
  );

export const hasAchProcessor = (processors) =>
  (processors ?? []).some(isAchProcessorId);

export const hasCcProcessor = (processors) =>
  (processors ?? []).some((id) => !isAchProcessorId(id));

export const hasSkipPricingMinimumProcessor = (processors) =>
  (processors ?? []).some((id) =>
    (config.paymentcloud.SKIP_PRICING_MINIMUM_PROCESSOR_IDS ?? []).some(
      (skipId) => Number(skipId) === Number(id),
    ),
  );

export const hasNorthTemplateId = (northTemplateId) => {
  if (northTemplateId == null || northTemplateId === "") return false;
  const n = Number(northTemplateId);
  return Number.isFinite(n) && n > 0;
};

export const shouldSkipPricingMinimums = (values) =>
  hasSkipPricingMinimumProcessor(values?.processors) ||
  hasNorthTemplateId(values?.north_template_id);

/**
 * The ACH processor auto-selected when ACH pricing is enabled.
 * If more than one ACH processor is ever added, this
 * has to become an explicit choice in the UI.
 */
export const primaryAchProcessorId = () => {
  const ids = config.paymentcloud.ACH_PROCESSOR_IDS ?? [];
  return ids[0];
};

/** True when every non-ACH processor is Kurv (Kurv-only CC, ACH ignored). */
export const isKurvOnlyCc = (processors) => {
  const ccIds = (processors ?? []).filter((id) => !isAchProcessorId(id));
  return ccIds.length > 0 && ccIds.every(isKurvProcessorId);
};

export const blankKurvOptionalTiers = () =>
  Object.fromEntries(KURV_OPTIONAL_TIERED_FIELDS.map((name) => [name, ""]));

export const defaultKurvOptionalTiers = (pricingDefaults = {}) =>
  Object.fromEntries(
    KURV_OPTIONAL_TIERED_FIELDS.map((name) => [
      name,
      pricingDefaults?.tiered?.[name]?.default ?? "",
    ]),
  );

/**
 * Pure decision for Kurv optional tiers. Returns "blank" | "restore" | "leave".
 * Exhaustively testable from four scalars — no patch object required.
 */
export const resolveKurvTierAction = ({
  pricingModel,
  wasKurvOnly,
  isKurvOnly,
  patchReseedsTiers,
}) => {
  if (pricingModel !== "tiered") return "leave";
  if (isKurvOnly && (!wasKurvOnly || patchReseedsTiers)) return "blank";
  if (wasKurvOnly && !isKurvOnly) return "restore";
  return "leave";
};

/**
 * Kurv optional-tier overlay only — callers spread their own patch first,
 * then this. Returns {} / blanks / org defaults.
 */
export const withKurvTierRules = ({
  values,
  nextProcessors = values.processors,
  pricingDefaults,
  patch = {},
}) => {
  const action = resolveKurvTierAction({
    pricingModel: patch.pricing_model ?? values.pricing_model,
    wasKurvOnly: isKurvOnlyCc(values.processors),
    isKurvOnly: isKurvOnlyCc(nextProcessors),
    //  Patch already re-seeds these fields with org defaults
    // (anything spreading getAllDefaultPricingModelValues does).
    patchReseedsTiers: KURV_OPTIONAL_TIERED_FIELDS.some((f) => f in patch),
  });

  if (action === "blank") return blankKurvOptionalTiers();
  if (action === "restore") return defaultKurvOptionalTiers(pricingDefaults);
  return {};
};

/**
 * - CC model already selected → ACH field defaults + add_ach: "yes"
 * - No pricing model yet → pricing_model: "ach", add_ach: "no", and seed
 *   other fees (setup_fee, …) from ach defaults. That seed runs ONLY in this
 *   ACH-only branch — not when attaching ACH onto an existing CC model.
 */
export const addAchPricingPatch = ({
  pricingModel,
  pricingDefaults,
  processors,
}) => {
  const achId = primaryAchProcessorId();
  const processorsToPatch = processors ?? [];
  const patch = {
    ...getAllDefaultACHValues(pricingDefaults),
    processors:
      !achId || hasAchProcessor(processorsToPatch)
        ? processorsToPatch
        : [...processorsToPatch, achId],
  };

  if (pricingModel !== "ach" && pricingModel !== "") {
    patch.add_ach = "yes";
    return patch;
  }

  if (!pricingModel) {
    patch.pricing_model = "ach";
    patch.add_ach = "no";
    Object.keys(otherDefaultFeesPrettyNames).forEach((key) => {
      patch[key] = pricingDefaults?.ach?.[key]?.default ?? 0;
    });
  }

  return patch;
};

export const removeAchPricingPatch = ({
  pricingModel,
  pricingDefaults,
  processors = [],
}) => {
  const withoutAch = {
    processors: (processors ?? []).filter((id) => !isAchProcessorId(id)),
  };

  if (pricingModel === "ach") {
    return {
      pricing_model: "",
      add_ach: "no",
      ...getAllDefaultPricingModelValues(pricingDefaults),
      ...withoutAch,
    };
  }

  return { add_ach: "no", ...withoutAch };
};

/**
 * Build the CC pricing patch (Add credit card button or CC processor auto-enable).
 * Defaults to dual_pricing. If ACH was primary, keep it as add_ach: "yes".
 */
export const addCCPricingPatch = ({ pricingModel, pricingDefaults }) => {
  const patch = {
    ...getAllDefaultPricingModelValues(pricingDefaults),
    pricing_model: "dual_pricing",
    ...(pricingModelRequiresDailyBilling("dual_pricing")
      ? { billing_sequence: "daily" }
      : {}),
  };
  if (pricingModel === "ach") {
    patch.add_ach = "yes";
  }
  Object.keys(otherDefaultFeesPrettyNames).forEach((key) => {
    patch[key] = pricingDefaults?.dual_pricing?.[key]?.default ?? 0;
  });
  return patch;
};

/**
 * Remove CC pricing and any CC processors. If ACH was an addon, promote it
 * to primary; otherwise clear the pricing model.
 */
export const removeCCPricingPatch = ({
  addAch,
  pricingDefaults,
  processors = [],
}) => {
  const withoutCc = {
    processors: (processors ?? []).filter(isAchProcessorId),
  };

  if (addAch === "yes") {
    const patch = {
      ...getAllDefaultPricingModelValues(pricingDefaults),
      pricing_model: "ach",
      add_ach: "no",
      ...withoutCc,
    };
    Object.keys(otherDefaultFeesPrettyNames).forEach((key) => {
      patch[key] = pricingDefaults?.ach?.[key]?.default ?? 0;
    });
    return patch;
  }

  return {
    ...getAllDefaultPricingModelValues(pricingDefaults),
    pricing_model: "",
    ...withoutCc,
  };
};

/**
 * Symmetric ACH enable/disable when an ACH processor is toggled.
 *
 * Selecting ACH with no CC pricing model → pricing_model = "ach"
 * (and other-fee seeds via addAchPricingPatch). Selecting ACH with a
 * CC model already chosen → add_ach: "yes" only.
 */
export const achPricingPatchForProcessorChange = ({
  values,
  nextProcessors,
  pricingDefaults,
}) => {
  const hadAch = hasAchProcessor(values.processors);
  const hasAch = hasAchProcessor(nextProcessors);
  const achPricingOn =
    values.add_ach === "yes" || values.pricing_model === "ach";

  if (hasAch && !hadAch && !achPricingOn) {
    return addAchPricingPatch({
      pricingModel: values.pricing_model,
      pricingDefaults,
      processors: nextProcessors,
    });
  }
  if (hadAch && !hasAch && achPricingOn) {
    return removeAchPricingPatch({
      pricingModel: values.pricing_model,
      pricingDefaults,
      processors: nextProcessors,
    });
  }
  return {};
};

/**
 * CC enable-only when a credit-card processor is toggled.
 *
 * Selecting a CC processor with no CC pricing → dual_pricing (and promote
 * ACH-primary to add_ach). Deselecting is deliberately NOT mirrored: the
 * patch would overwrite saved rates with org defaults, which is unrecoverable
 * on the edit-pricing page. The `cc-pricing-needs-cc-processor` test in
 * processorInfoSchema surfaces the leftover CC pricing instead, and the
 * explicit Remove button in the pricing section still clears it.
 */
export const ccPricingPatchForProcessorChange = ({
  values,
  nextProcessors,
  pricingDefaults,
}) => {
  const hadCc = hasCcProcessor(values.processors);
  const hasCc = hasCcProcessor(nextProcessors);
  const ccPricingOn =
    Boolean(values.pricing_model) && values.pricing_model !== "ach";

  if (hasCc && !hadCc && !ccPricingOn) {
    return addCCPricingPatch({
      pricingModel: values.pricing_model,
      pricingDefaults,
    });
  }
  return {};
};
